import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BUSINESS } from "@/lib/api";
import ServiceAreaMap from "@/components/ServiceAreaMap";

export default function Footer() {
  return (
    <footer className="bg-white border-t border-slate-200 mt-24">
      {/* Service area map strip */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              Service Area
            </p>
            <h2 className="pf-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Serving metro Atlanta&rsquo;s north arc.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We service five metro Atlanta counties: <strong>Fulton</strong>,&nbsp;
              <strong>DeKalb</strong>, <strong>Cobb</strong>,&nbsp;
              <strong>Gwinnett</strong> and <strong>Forsyth</strong>. Not sure
              if you&rsquo;re in range? Give us a call — we often accommodate
              neighbors just past the line.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://www.google.com/maps/search/?api=1&query=The+Phoenix+Fix+Atlanta+GA"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-gmb-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
              >
                <MapPin className="h-4 w-4" />
                View on Google Maps
              </a>
              <a
                href={BUSINESS.phoneHref}
                data-testid="footer-call-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 border border-slate-200 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {BUSINESS.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ServiceAreaMap />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 overflow-hidden">
              <img src="/phoenix-icon.png" alt="" className="h-full w-full object-cover" />
            </span>
            <span className="pf-heading text-lg font-semibold text-slate-900">
              The Phoenix Fix
            </span>
          </div>
          <p className="mt-4 text-sm text-slate-500 leading-relaxed">
            Professional, dependable handyman services across metro Atlanta.
            Big or small — we fix it right the first time.
          </p>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
            Explore
          </p>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" className="text-slate-700 hover:text-blue-700" data-testid="footer-link-home">
                Home
              </Link>
            </li>
            <li>
              <Link to="/booking" className="text-slate-700 hover:text-blue-700" data-testid="footer-link-booking">
                Book a Service
              </Link>
            </li>
            <li>
              <Link to="/specials" className="text-slate-700 hover:text-blue-700" data-testid="footer-link-specials">
                Specials
              </Link>
            </li>
            <li>
              <Link to="/join" className="text-slate-700 hover:text-blue-700" data-testid="footer-link-join">
                Join the Team
              </Link>
            </li>
            <li>
              <Link to="/admin/login" className="text-slate-500 hover:text-blue-700" data-testid="footer-link-admin">
                Admin Portal
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
            Contact
          </p>
          <ul className="space-y-3 text-sm text-slate-700">
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-blue-700" />
              <a href={BUSINESS.phoneHref} data-testid="footer-phone">{BUSINESS.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-blue-700" />
              <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email">{BUSINESS.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-blue-700" />
              <span data-testid="footer-hours">{BUSINESS.hours}</span>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
            Service Area
          </p>
          <div className="flex items-start gap-3 text-sm text-slate-700">
            <MapPin className="h-4 w-4 text-blue-700 mt-0.5" />
            <p data-testid="footer-area">{BUSINESS.area}</p>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} The Phoenix Fix. All rights reserved.
          </p>
          <p className="text-xs text-slate-400">
            Licensed-style craftsmanship • Locally owned and operated
          </p>
        </div>
      </div>
    </footer>
  );
}
